import Cookies from 'js-cookie';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import { getCredentials, userSignout } from '../../actions/hub/hubAuthPagesActions';
import { FullPageLoader } from '../../components/Loader/Loader';
import { isDataLoadedToInitApp } from '../../selectors/hubSelector';
import {
  AUTHENTICATION_STATUS,
  LOCALSTORAGE_KEEP_ME_LOGGED_IN,
  LOCALSTORAGE_SSO_HANDOFF_SESSION,
} from '../../util/constants';
import { PATH_HUB_BASE, PATH_HUB_HOME, PATH_HUB_LOG_IN, PATH_HUB_ONBOARDING } from '../../util/pagePath';
import withAppShell from '../../v2/app-shell/AppShell';
import withGlobalInfobar from '../../v2/common/global-infobar/withGlobalInfobar';
import PageError from '../PageError';
import LeadRerouter from './LeadRerouter';

const ProtectedRoute = (props) => {
  const { authenticationStatus, checkUserStatus, signOut, location, isClockSkewDetected, isDataReady, path } = props;

  // Wrap every protected page in the global app shell (sidebar + content region).
  // The global infobar is only composed onto the home page. Memoised on the page
  // component so the shell (and its sidebar open/collapsed state) is not remounted
  // on every render.
  const WrappedComponent = React.useMemo(() => {
    const isHomePage = path === PATH_HUB_HOME || path === PATH_HUB_BASE;
    const component = isHomePage ? withGlobalInfobar(props.component) : props.component;
    return withAppShell(component);
  }, [props.component, path]);

  // Only bootstrap credentials on cold start (INIT). Calling getCredentials while
  // UN_AUTHENTICATED (e.g. mid-logout) can revive an SSO-seeded Cognito session.
  if (authenticationStatus === AUTHENTICATION_STATUS.INIT) {
    const isSsoHandoffSession = localStorage.getItem(LOCALSTORAGE_SSO_HANDOFF_SESSION) === 'true';

    // Note, when landing on onboarding page with KEEP_ME_LOGGED_IN unticked,
    // since user is still UNAUTHENTICATED, they will be redirected to log in form.
    // That's why we need to exclude onboarding page.
    // SSO handoff sessions skip this force-sign-out and re-validate tokens instead.
    if (
      !isSsoHandoffSession &&
      localStorage.getItem(LOCALSTORAGE_KEEP_ME_LOGGED_IN) === 'false' &&
      location.pathname !== PATH_HUB_ONBOARDING
    ) {
      signOut();
      return;
    }

    const staffToken = Cookies.get('staffToken');
    // Note: staff login issue fix -
    if (staffToken) {
      signOut();
    } else {
      // Validates Cognito tokens (including SSO-seeded sessions) via Amplify.
      checkUserStatus();
    }
  }

  if (isClockSkewDetected) {
    return <PageError activePage="redirect-clock-skew-handle" />;
  }

  return (
    <Fragment>
      {(authenticationStatus === AUTHENTICATION_STATUS.INIT || !isDataReady) && <FullPageLoader />}
      {authenticationStatus === AUTHENTICATION_STATUS.AUTHENTICATED && isDataReady && (
        <LeadRerouter>
          <Route path={path} component={WrappedComponent} />
        </LeadRerouter>
      )}
      {(authenticationStatus === AUTHENTICATION_STATUS.UN_AUTHENTICATED ||
        authenticationStatus === AUTHENTICATION_STATUS.AUTHENTICATION_ERROR) && <Redirect to={PATH_HUB_LOG_IN} />}
    </Fragment>
  );
};

ProtectedRoute.propTypes = {
  authenticationStatus: PropTypes.string.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  isClockSkewDetected: PropTypes.bool.isRequired,
  isDataReady: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  component: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
  path: PropTypes.string.isRequired,
  checkUserStatus: PropTypes.func.isRequired,
  signOut: PropTypes.func.isRequired,
};

export default connect(
  (state) => {
    const { login } = state;
    return {
      authenticationStatus: login.authenticationStatus,
      isClockSkewDetected: login.isClockSkewDetected,
      isDataReady: isDataLoadedToInitApp(state),
    };
  },
  (dispatch) => {
    return {
      checkUserStatus: bindActionCreators(getCredentials, dispatch),
      signOut: bindActionCreators(userSignout, dispatch),
    };
  }
)(ProtectedRoute);
